import React, { useState } from 'react';
import APModal from 'components/Modals/APModal/APModal';
import useAlphaSnackbar from 'hooks/useAlphaSnackbar';
import { Trans, useTranslation } from 'react-i18next';
import PaymentsService from 'services/Payments/payments.service';

import { PaymentDto } from '@alpha/payments-dtos';
import { Alert } from '@alpha/ui-lib/ui/Alert/Alert';
import { ActionButton } from '@alpha/ui-lib/ui/button';
import { Box, Divider } from '@alpha/ui-lib/ui/external';
import { StyledKeyValueTable } from '@alpha/ui-lib/ui/table';
import { Typography } from '@alpha/ui-lib/ui/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useVOPConfirmation from './useVOPConfirmation';
import useStyles from './VOPConfirmationModal.styles';
import formatIsoDate from 'utils/formatIsoDate';

type VOPConfirmationModalProps = {
  payment: PaymentDto | undefined;
  open: boolean;
  handleClose: () => void;
  handleActionButton?: (data: {
    paymentId: string;
    vopDetails?: PaymentDto['vopDetails'];
    vopRequired?: boolean;
  }) => void;
  readOnly?: boolean;
  handleAcknowledged?: () => void;
};

const VOPConfirmationModal = ({
  payment,
  open,
  handleClose,
  handleActionButton,
  readOnly = false,
  handleAcknowledged,
}: VOPConfirmationModalProps): JSX.Element => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { icon, title, message, color, closeMatchTable, matchedTable } =
    useVOPConfirmation(payment);

  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const sb = useAlphaSnackbar();

  const acknowledgeAndContinue = (id: string) => {
    setIsAcknowledging(true);
    PaymentsService.patchAcknowledgePayment(id)
      .then(() => {
        handleActionButton?.({
          paymentId: id,
          vopDetails: payment?.vopDetails,
          vopRequired: payment?.vopRequired,
        });
      })
      .catch((error) => {
        console.error(error);
        sb.trigger(t('error_default'), 'error');
      })
      .finally(() => {
        setIsAcknowledging(false);
        handleAcknowledged?.();
      });
  };

  const vopApprover = payment?.vopDetails?.vopApproverName;
  const vopDate = payment?.vopDetails?.vopApprovalDateTime
    ? formatIsoDate(payment?.vopDetails?.vopApprovalDateTime)
    : '-';

  return (
    <APModal.Container
      open={open}
      onClose={handleClose}
      className={styles.container}
    >
      <>
        <APModal.Header
          onClose={handleClose}
          className={styles.header}
          showCloseButton
        >
          <Box className={styles.header}>
            <Typography variant='h2'>
              <FontAwesomeIcon
                icon={icon}
                color={color}
                size='sm'
                style={{ marginRight: '8px' }}
              />
              {title}
            </Typography>
            <Typography variant='body1'>
              <Trans
                components={{ strong: <strong /> }}
                values={{
                  name: payment?.beneficiaryName ?? '',
                  correct_name: payment?.vopDetails?.name ?? '',
                }}
              >
                {message}
              </Trans>
            </Typography>
          </Box>
        </APModal.Header>
        <APModal.Wrapper className={styles.wrapper}>
          <Box className={styles.body}>
            <Typography variant='h3' style={{ position: 'relative' }} />

            {closeMatchTable.length > 0 && (
              <>
                {/* Showing what's different from the provided details */}
                <StyledKeyValueTable
                  testId='close-match-table'
                  className={styles.noDivider}
                  data={closeMatchTable}
                />

                <Divider
                  style={{
                    marginTop: '20px',
                    backgroundColor: '#f7f7f7',
                    marginBottom: '24px',
                  }}
                />
              </>
            )}

            {/* Showing same / completely different details  */}
            <StyledKeyValueTable
              testId='matching-table'
              className={[styles.noDivider, styles.matchingTable].join(' ')}
              data={matchedTable}
            />

            {!readOnly && (
              <Box className={styles.vopWarning}>
                <Alert
                  variant='warning'
                  text={
                    <>
                      <strong>{t('please_note')}</strong>{' '}
                      {t('cop_alert_message')}
                    </>
                  }
                />
              </Box>
            )}

            {readOnly && payment?.vopDetails?.vopApprovalAcknowledged && (
              <Box className={styles.vopInfo}>
                <Alert
                  variant='info'
                  text={
                    <Typography>
                      {<strong>{t('please_note')}</strong>}{' '}
                      {t('cop_alert_message_acknowledged')} {vopApprover}{' '}
                      {t('on')} {vopDate}.
                    </Typography>
                  }
                />
              </Box>
            )}
          </Box>
        </APModal.Wrapper>

        <Box className={styles.buttonsContainer}>
          {!readOnly && (
            <>
              <ActionButton onClick={handleClose} className='cancelButton'>
                {t('go_back')}
              </ActionButton>
              <ActionButton
                testId='modal-action-button'
                disabled={isAcknowledging}
                onClick={() => {
                  if (payment?.id) {
                    acknowledgeAndContinue(payment.id);
                  }
                }}
                className='submitButton'
                loading={isAcknowledging}
              >
                {t('Acknowledge & Continue')}
              </ActionButton>
            </>
          )}

          {readOnly && (
            <ActionButton
              onClick={handleClose}
              className='cancelButton'
              fullWidth
            >
              {t('close')}
            </ActionButton>
          )}
        </Box>
      </>
    </APModal.Container>
  );
};

export default VOPConfirmationModal;
